import { useTranscriptionsState } from '@modules/transcriptions/useTranscriptionsState';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Note } from 'src/@types/notes-transcription';
import Iconify from 'src/components/iconify';
import { SkeletonConversationItem } from 'src/components/skeleton';
import { useLocales } from 'src/locales';
import { useTranscriptionNotesState } from '../../Gathering/GatheringTranscription/useTranscriptionNotesState';
import { useNotesTranscriptionModal } from '../TranscriptionModal';
import { CoperaAiTranscriptionActions } from './CoperaAiTranscriptionActions/CoperaAiTranscriptionActions';
import { useTranscriptionQuery } from './useTranscriptionQuery';

type Props = {
  note: Note;
  onAddToEditor: (text: string) => void;
  autoSummaryText?: string;
};

export const NotesAiTranscriptionInner = ({ note, onAddToEditor }: Props) => {
  const { activeTranscriptionNote } = useTranscriptionsState();
  const { showNotesTranscriptionModal } = useNotesTranscriptionModal();

  const { isTranscriptionStarted } = useTranscriptionNotesState();
  const { translate } = useLocales();

  const { transcription, isFetching } = useTranscriptionQuery({
    transcriptionId: note.noteRef as string,
    noteId: note._id,
  });

  const handleOpenTranscription = () => {
    if (!transcription || !note.channelRef) return;
    showNotesTranscriptionModal({
      transcription,
      channelId: note.channelRef,
      noteId: note._id,
    });
  };

  if (isTranscriptionStarted && note._id === activeTranscriptionNote?._id)
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <Iconify
          icon="icon-park-outline:loading"
          color={(theme) => theme.palette.primary.main}
        />
        <Typography variant="subtitle2" color="text.secondary">
          {translate('actions_bar.copera_taking_notes')}
        </Typography>
      </Stack>
    );

  if (note.loadingTranscription)
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <Iconify
          icon="icon-park-outline:loading"
          color={(theme) => theme.palette.primary.main}
        />
        <Typography variant="subtitle2" color="text.secondary">
          {translate('actions_bar.loading_transcription')}
        </Typography>
      </Stack>
    );

  if (isFetching)
    return <SkeletonConversationItem width={1} sx={{ py: 0.75, px: 1 }} />;

  if (!note.noteRef)
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <Iconify
          icon="tabler:alert-triangle"
          color={(theme) => theme.palette.primary.main}
        />
        <Typography variant="subtitle2" color="text.secondary">
          {translate('actions_bar.no_transcription_found')}
        </Typography>
      </Stack>
    );

  return (
    <>
      <Button sx={{ gap: 1 }} onClick={handleOpenTranscription}>
        <Iconify icon="tabler:text-recognition" color="text.primary" />
        <Typography
          variant="subtitle2"
          sx={(theme) => ({
            borderBottom: `1px solid ${theme.palette.text.primary}`,
          })}
          color="text.primary"
        >
          {translate('actions_bar.transcription', { title: note.title })}
        </Typography>
      </Button>
      <Box py={0.375}>
        <CoperaAiTranscriptionActions
          onAddToEditor={onAddToEditor}
          transcriptionId={note.noteRef}
        />
      </Box>
    </>
  );
};

export const AiTranscription = ({ note, onAddToEditor }: Props) => {
  return (
    <Stack
      direction="row"
      borderBottom="1px solid"
      borderColor="divider"
      alignItems="center"
      justifyContent="space-between"
      p={2}
    >
      <NotesAiTranscriptionInner note={note} onAddToEditor={onAddToEditor} />
    </Stack>
  );
};
